import { createSelector } from 'reselect';
import get from 'lodash/get';
import { takeEvery, put } from 'redux-saga/effects';

import * as leaveFamilyCircle from '../circles/leaveFamilyCircle';
import { consts as inviteCaregiverFamilyConsts } from '../circles/inviteCaregiverFamily';
import { consts as removeAssignedProviderConsts } from '../circles/removeAssignedProvider';
import { consts as reqSocialChannel } from '../board/reqSocialChannel';
import { consts as leaveCommunityConsts } from '../communities/leaveCommunity';
import { consts as reqBlockUserConsts } from '../board/reqBlockUser';
import { consts as confirmVisitGWConsts } from '../appointmentForm/requests/confirm-visit-GW-req';
import { consts as confirmVisitSelfSchedulingConsts } from '../appointmentForm/requests/confirm-visit-selfScheduling-req';
import { consts as schedulingsConsts } from '../appointmentForm/requests/schedulings-req';
import { consts as cancelAppointmentConsts } from '../healthboard/requests/cancel-appointment-req';
import { consts as checkinAppointmentConsts } from '../healthboard/requests/check-in-appointment-req';
import { consts as reqAssignPatient } from '../board/reqAssignPatient';
import { consts as addDependent } from '../dependentForm/personal-info-req';
import { consts as inlineVisitTimeSlots } from '../appointmentForm/requests/inline-visits-time-slots';
import { consts as confirmInlineVisit } from '../appointmentForm/requests/confirm-inline-visit';
import { consts as createEirUrgentCareAppointmentConsts } from '../appointmentForm/requests/create-eir-urgent-care-appointment';
import * as changeAppointmentStatus from '../healthboard/requests/status-change-req';
import * as addPatientByProvider from '../profiles/addPatientByProvider';
import { consts as deleteCardConsts } from '../payment-methods/requests/delete-card-req';

import { createConsts, createActions } from '../../utils/redux';

import { ERROR_ASSIGN_CAREGIVER, CHAT } from '../../constants/strings';
import {
  ERROR_CODES,
  CHANGE_APPOINTMENT_STATUS_ERROR_CODES,
} from '../../constants/errors';

const supportedActions = ['UPDATE'];

export const consts = createConsts('ui', 'error', ...supportedActions);
export const actions = createActions(consts);

export const errorSelector = state => get(state, 'ui.error');
export const errorMsgSelector = createSelector(
  errorSelector,
  state => get(state, 'message', null),
);
export const errorIsStaticFlowSelector = createSelector(
  errorSelector,
  state => get(state, 'isStaticFlow', false),
);
export const infoErrorIconSelector = createSelector(
  errorSelector,
  state => get(state, 'infoErrorIcon', null),
);

const initialState = {
  message: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case consts.UPDATE:
      return {
        ...state,
        message: action.payload,
        isStaticFlow: action.isStaticFlow,
        infoErrorIcon: action.infoErrorIcon,
      };
    default:
      return state;
  }
};

const getErrorMsg = (actionType, message) => {
  const errorCode = get(message, 'error.code');

  const accessDenied = errorCode === ERROR_CODES.ACCESS_DENIED;

  if (CHANGE_APPOINTMENT_STATUS_ERROR_CODES.includes(errorCode)) {
    const description = get(message, 'error.description');

    return description;
  }

  if (
    actionType === removeAssignedProviderConsts.REJECTED ||
    (actionType === leaveFamilyCircle.consts.REJECTED && accessDenied) ||
    actionType === addDependent.REJECTED
  ) {
    return get(message, 'error.description');
  }

  if (actionType === inviteCaregiverFamilyConsts.REJECTED) {
    return ERROR_ASSIGN_CAREGIVER;
  }

  if (actionType === changeAppointmentStatus.consts.REJECTED) {
    const id =
      errorCode === ERROR_CODES.UNAVAILABLE_VIRTUAL_ROOM
        ? 'errors.appointment.unavailableVirtualRoom'
        : 'errors.common';
    return { id };
  }

  if (actionType === reqSocialChannel.REJECTED) {
    return CHAT.titles.newChatError;
  }

  if (
    [
      confirmVisitGWConsts.SHOW_ERROR_ALERT,
      confirmVisitSelfSchedulingConsts.SHOW_ERROR_ALERT,
      inlineVisitTimeSlots.SHOW_ERROR_ALERT,
      confirmInlineVisit.SHOW_ERROR_ALERT,
      createEirUrgentCareAppointmentConsts.SHOW_ERROR_ALERT,
    ].includes(actionType)
  ) {
    return { id: message };
  }

  if (
    actionType === cancelAppointmentConsts.REJECTED ||
    actionType === checkinAppointmentConsts.REJECTED
  ) {
    return { id: 'errors.commonErrorOccured' };
  }

  if (
    [
      schedulingsConsts.REJECTED,
      createEirUrgentCareAppointmentConsts.REJECTED,
    ].includes(actionType)
  ) {
    const id =
      errorCode === ERROR_CODES.APPOINTMENT_SCHEDULE_LIST_SERVICE_FAIL
        ? 'errors.appointment.thirdPartyError'
        : 'errors.common';
    return { id };
  }

  if (
    actionType === leaveCommunityConsts.REJECTED &&
    errorCode === ERROR_CODES.LAST_COMMUNITY_MODERATOR
  ) {
    return { id: 'community.menu.removeLastCommunityModerator' };
  }
  if (actionType === reqBlockUserConsts.REJECTED && accessDenied) {
    return { id: 'chat.title.blockedUserError' };
  }

  if (actionType === reqAssignPatient.REJECTED) {
    const providerAlreadyAssigned = get(
      message,
      'error.meta.validationErrors.base',
      [],
    ).some(({ code }) => code === ERROR_CODES.PROVIDER_ALREADY_ASSIGNED);
    const id = providerAlreadyAssigned
      ? 'circles.provider.alreadyAssigned'
      : 'errors.common';
    return { id };
  }

  if (actionType === addPatientByProvider.consts.ERROR) {
    return { id: 'errors.validation.alreadyExists' };
  }

  if (
    [deleteCardConsts.REJECTED, deleteCardConsts.ERROR_ENCOUNTERED].includes(
      actionType,
    )
  ) {
    return { id: 'profile.payment.removeErrorMessage' };
  }
};

function* updateErrorMsgWorker(action) {
  yield put({
    type: consts.UPDATE,
    payload: getErrorMsg(action.type, action.payload),
  });
}

export function* updateErrorMsgSaga() {
  yield takeEvery(
    [
      removeAssignedProviderConsts.REJECTED,
      inviteCaregiverFamilyConsts.REJECTED,
      reqSocialChannel.REJECTED,
      leaveCommunityConsts.REJECTED,
      reqBlockUserConsts.REJECTED,
      schedulingsConsts.REJECTED,
      confirmVisitGWConsts.SHOW_ERROR_ALERT,
      confirmVisitSelfSchedulingConsts.SHOW_ERROR_ALERT,
      cancelAppointmentConsts.REJECTED,
      checkinAppointmentConsts.REJECTED,
      leaveFamilyCircle.consts.REJECTED,
      changeAppointmentStatus.consts.REJECTED,
      reqAssignPatient.REJECTED,
      addPatientByProvider.consts.ERROR,
      addDependent.REJECTED,
      inlineVisitTimeSlots.SHOW_ERROR_ALERT,
      confirmInlineVisit.SHOW_ERROR_ALERT,
      createEirUrgentCareAppointmentConsts.REJECTED,
      deleteCardConsts.REJECTED,
      deleteCardConsts.ERROR_ENCOUNTERED,
    ],
    updateErrorMsgWorker,
  );
}



// WEBPACK FOOTER //
// ./src/redux/ui/error.js